import type { CreateReportCommentInput, ReportComment } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { ResourceListPage } from '#client/components/ui/ResourceListPage';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { formatDateTime } from '#client/lib/format';
import { type PhraseCatalogue, REPORT_COMMENTS, REPORT_REMARKS } from './catalogues';

/** A comment and a remark have the same shape on the wire. */
type Phrase = ReportComment;

const columns: Column<Phrase>[] = [
  { key: 'code', header: 'Code', render: (row) => <strong>{row.code}</strong>, width: '180px' },
  { key: 'description', header: 'Description', render: (row) => row.description },
];

function PhraseListPage({ catalogue }: { catalogue: PhraseCatalogue }) {
  return (
    <ResourceListPage<Phrase>
      title={catalogue.title}
      subtitle={catalogue.subtitle}
      path={catalogue.path}
      columns={columns}
      noun={catalogue.noun}
      nounPlural={catalogue.nounPlural}
      searchPlaceholder="Search by code or description"
      rowLabel={(row) => row.code}
      createCapability={`${catalogue.permission}.create`}
      editCapability={`${catalogue.permission}.edit`}
      deleteCapability={`${catalogue.permission}.delete`}
    />
  );
}

function PhraseDetailPage({ catalogue }: { catalogue: PhraseCatalogue }) {
  return (
    <ResourceDetailPage<Phrase>
      path={catalogue.path}
      noun={catalogue.noun}
      editCapability={`${catalogue.permission}.edit`}
      deleteCapability={`${catalogue.permission}.delete`}
      title={(phrase) => phrase.code}
      subtitle={() => `Report ${catalogue.noun}`}
      deleteLabel={(phrase) => phrase.code}
    >
      {(phrase) => (
        <Card>
          <DetailList
            items={[
              { label: 'Code', value: phrase.code },
              { label: 'Created', value: formatDateTime(phrase.created_at) },
              { label: 'Last updated', value: formatDateTime(phrase.updated_at) },
              { label: 'Description', value: phrase.description, wide: true },
            ]}
          />
        </Card>
      )}
    </ResourceDetailPage>
  );
}

function PhraseFormPage({ catalogue }: { catalogue: PhraseCatalogue }) {
  return (
    <ResourceFormShell<Phrase> path={catalogue.path} noun={catalogue.noun}>
      {({ id, record }) => <PhraseForm catalogue={catalogue} id={id} record={record} />}
    </ResourceFormShell>
  );
}

/** Mounted once the record is loaded, so `useState` initialises from it. */
function PhraseForm({
  catalogue,
  id,
  record,
}: {
  catalogue: PhraseCatalogue;
  id?: string | undefined;
  record?: Phrase | undefined;
}) {
  const { values, set } = useFormValues<CreateReportCommentInput>({
    code: record?.code ?? '',
    description: record?.description ?? '',
  });
  const form = useResourceForm({
    path: catalogue.path,
    id,
    schema: catalogue.schema,
    noun: catalogue.label,
  });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <Card>
          <TextField
            label="Code"
            value={values.code}
            error={form.errors.code}
            onChange={(event) => set('code', event.target.value)}
            helpText="A short unique identifier, e.g. CBC-NORMAL"
            required
          />

          <TextArea
            label="Description"
            value={values.description}
            error={form.errors.description}
            onChange={(event) => set('description', event.target.value)}
            rows={5}
            required
          />
        </Card>

        <FormActions
          cancelTo={catalogue.path}
          submitLabel={form.isEdit ? 'Save changes' : `Create ${catalogue.noun}`}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}

export const ReportCommentListPage = () => <PhraseListPage catalogue={REPORT_COMMENTS} />;
export const ReportCommentDetailPage = () => <PhraseDetailPage catalogue={REPORT_COMMENTS} />;
export const ReportCommentFormPage = () => <PhraseFormPage catalogue={REPORT_COMMENTS} />;
export const ReportRemarkListPage = () => <PhraseListPage catalogue={REPORT_REMARKS} />;
export const ReportRemarkDetailPage = () => <PhraseDetailPage catalogue={REPORT_REMARKS} />;
export const ReportRemarkFormPage = () => <PhraseFormPage catalogue={REPORT_REMARKS} />;
